/**
 * What a list of programmes filters on, as data.
 *
 * The predicates are the ones `useProgrammeFilters` has always used; this is
 * the same rules declared rather than wired, so a screen hands them to
 * `useCollectionFilters` and gets the panel, the count and the clear control
 * without composing any of it.
 *
 * @module components/shared/programme-filter-defs
 */

import type { FilterDef, FilterOption } from "@/components/ui";
import type { ProgrammeSummary } from "@/platform";

import {
  matchesText,
  matchesWorkflow,
  type ProgrammeWorkflowFilter,
  UNSPECIFIED_DISCIPLINE_FILTER,
  WORKFLOW_OPTIONS,
} from "./programme-filter-rules";

/** The value standing in for "this programme has no level set". */
const UNSPECIFIED_LEVEL = "unspecified";

/** Overrides the control ids, for screens whose specs already name them. */
export type ProgrammeFilterTestIds = Partial<
  Record<"workflow" | "level" | "discipline" | "text", string>
>;

function levelOptions(programmes: readonly ProgrammeSummary[]): readonly FilterOption[] {
  const levels = [...new Set(programmes.map((programme) => programme.nfqLevel))]
    .filter((level): level is number => level !== null)
    .sort((a, b) => a - b);
  const unspecified = programmes.some((programme) => programme.nfqLevel === null);
  return [
    ...levels.map((level) => ({ value: String(level), label: `NFQ ${level}` })),
    ...(unspecified ? [{ value: UNSPECIFIED_LEVEL, label: "Level not set" }] : []),
  ];
}

function disciplineOptions(programmes: readonly ProgrammeSummary[]): readonly FilterOption[] {
  const named = [
    ...new Set(programmes.map((programme) => (programme.discipline ?? "").trim()).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));
  const unspecified = programmes.some((programme) => (programme.discipline ?? "").trim() === "");
  return [
    ...named.map((discipline) => ({ value: discipline, label: discipline })),
    ...(unspecified ? [{ value: UNSPECIFIED_DISCIPLINE_FILTER, label: "Discipline not set" }] : []),
  ];
}

/**
 * @example
 * ```tsx
 * const { rows, filtering } = useCollectionFilters(programmes, programmeFilterDefs(), {
 *   label: "Filter programmes",
 *   noun: "programme",
 * });
 * ```
 */
export function programmeFilterDefs(
  testIds: ProgrammeFilterTestIds = {},
): readonly FilterDef<ProgrammeSummary>[] {
  return [
    {
      kind: "select",
      key: "workflow",
      // Not "Filter programmes": the panel is already headed that, and a field
      // repeating its own panel's name says nothing about what it filters.
      label: "State",
      help: "Filter by workflow or delivery state.",
      allLabel: "All states",
      options: WORKFLOW_OPTIONS.filter((option) => option.value !== "all"),
      match: (programme, value) => matchesWorkflow(programme, value as ProgrammeWorkflowFilter),
      "data-testid": testIds.workflow,
    },
    {
      kind: "select",
      key: "level",
      label: "Level",
      help: "Limit results by NFQ level.",
      allLabel: "All levels",
      options: levelOptions,
      match: (programme, value) =>
        value === UNSPECIFIED_LEVEL
          ? programme.nfqLevel === null
          : String(programme.nfqLevel) === value,
      "data-testid": testIds.level,
    },
    {
      kind: "select",
      key: "discipline",
      label: "Discipline",
      help: "Filter by primary discipline classification.",
      allLabel: "All disciplines",
      options: disciplineOptions,
      match: (programme, value) => {
        const discipline = (programme.discipline ?? "").trim();
        return value === UNSPECIFIED_DISCIPLINE_FILTER ? discipline === "" : discipline === value;
      },
      "data-testid": testIds.discipline,
    },
    {
      kind: "text",
      key: "text",
      label: "Search",
      help: "Search by title, award, state, revision, or discipline.",
      placeholder: "Title, award, state, revision",
      match: (programme, query) =>
        matchesText(programme, query, (programme.discipline ?? "").trim()),
      "data-testid": testIds.text,
    },
  ];
}
